'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { loadSessionHomePath } from '@/features/authentication/presentation/composables/utils/session-navigation';
import { useBillingInvoices } from '@/features/billing-invoices/presentation/context/BillingInvoicesContext';
import { useReminderSettings } from '@/features/app/reminder-settings/presentation/context/ReminderSettingsContext';
import { useSubscription } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { useUser } from '@/features/app/user/presentation/context/UserContext';
import { VERIFY_SUBSCRIPTION_PAYMENT_ID_QUERY } from '@/features/payments/presentation/composables/constants/verify-subscription.constants';
import { useVerifySubscriptionPayment } from '@/features/payments/presentation/composables/hooks/useVerifySubscriptionPayment';
import type { VerifySubscriptionViewProps } from '@/features/payments/presentation/components/VerifySubscriptionView';
import {
  VERIFY_SUBSCRIPTION_BACK_CTA,
  VERIFY_SUBSCRIPTION_TITLE,
} from '@/features/payments/presentation/constants/verify-subscription-copy';

export type VerifySubscriptionBridgeResult = {
  layout: { title: string };
  view: VerifySubscriptionViewProps;
  footer: {
    visible: boolean;
    label: string;
    onBackToPlans: () => void;
  };
};

export function useVerifySubscriptionBridge(): VerifySubscriptionBridgeResult {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const { state: userState, events: userEvents } = useUser();
  const { events: subscriptionEvents } = useSubscription();
  const { events: reminderEvents } = useReminderSettings();
  const { events: billingEvents } = useBillingInvoices();

  const paymentIdParam = searchParams.get(VERIFY_SUBSCRIPTION_PAYMENT_ID_QUERY);
  const userId = userState.currentUser?.id ?? authState.user?.uid ?? undefined;

  useEffect(() => {
    if (!authState.authReady) return;
    if (!authState.user?.uid) {
      navigate(ROUTES.LOGIN, { replace: true });
    }
  }, [authState.authReady, authState.user?.uid, navigate]);

  const onRedirectToApp = useCallback(async () => {
    const uid = authState.user?.uid;
    if (!uid) return;
    const path = await loadSessionHomePath({
      uid,
      getUserById: userEvents.getUserById,
      getSubscriptionByUserId: subscriptionEvents.getSubscriptionByUserId,
      getReminderSettingsByUserId: reminderEvents.getByUserId,
    });
    navigate(path, { replace: true });
  }, [
    authState.user?.uid,
    navigate,
    userEvents,
    subscriptionEvents,
    reminderEvents,
  ]);

  const { phase, detailMessage } = useVerifySubscriptionPayment({
    paymentIdParam,
    authReady: authState.authReady,
    userId,
    subscribePaymentDoc: billingEvents.subscribePaymentDoc,
    onRedirectToApp,
  });

  const onBackToPlans = useCallback(() => {
    navigate(ROUTES.CHOOSE_PLAN, { replace: true });
  }, [navigate]);

  const view = useMemo((): VerifySubscriptionViewProps => {
    const authBlocking = !authState.authReady || !authState.user?.uid;
    return {
      authBlocking,
      phase: authBlocking ? 'listening' : phase,
      detailMessage: authBlocking ? null : detailMessage,
    };
  }, [authState.authReady, authState.user?.uid, phase, detailMessage]);

  const footerVisible = useMemo(() => {
    if (!authState.authReady || !authState.user?.uid) return false;
    return (
      phase === 'invalid_payment_id' ||
      phase === 'not_found' ||
      phase === 'listen_error' ||
      phase === 'payment_failed' ||
      phase === 'unauthenticated'
    );
  }, [authState.authReady, authState.user?.uid, phase]);

  return useMemo(
    () => ({
      layout: { title: VERIFY_SUBSCRIPTION_TITLE },
      view,
      footer: {
        visible: footerVisible,
        label: VERIFY_SUBSCRIPTION_BACK_CTA,
        onBackToPlans,
      },
    }),
    [view, footerVisible, onBackToPlans]
  );
}
